import { logger } from '@/lib/utils/logger';
/**
 * Standard error handling utilities for consistent API error management
 */

/**
 * Standard result type - always has data OR error
 */
export type ApiResult<T> = {
  data: T | null;
  error: Error | null;
};

/**
 * Custom error class for API errors with additional context
 */
export class ApiError extends Error {
  public readonly operation: string;
  public readonly statusCode?: number;
  public readonly originalError?: unknown;

  constructor(
    message: string,
    operation: string,
    statusCode?: number,
    originalError?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.operation = operation;
    this.statusCode = statusCode;
    this.originalError = originalError;
  }
}

/**
 * Handle API errors consistently
 */
export function handleApiError<T>(
  operation: string,
  error: unknown
): ApiResult<T> {
  const errorMsg = error instanceof Error
    ? error.message
    : String(error);

  // Only log errors in development or when explicitly needed
  if (process.env.NODE_ENV === 'development') {
    logger.error(`❌ Error in ${operation}:`, error);
  }

  // Create a proper error object
  const apiError = error instanceof ApiError
    ? error
    : new ApiError(errorMsg, operation, undefined, error);

  return {
    data: null,
    error: apiError
  };
}

/**
 * Success result wrapper
 */
export function success<T>(data: T): ApiResult<T> {
  return { data, error: null };
}

/**
 * Check if a result is successful
 */
export function isSuccess<T>(result: ApiResult<T>): result is { data: T; error: null } {
  return result.error === null && result.data !== null;
}

/**
 * Check if a result is an error
 */
export function isError<T>(result: ApiResult<T>): result is { data: null; error: Error } {
  return result.error !== null;
}

/**
 * Unwrap result or throw error (for cases where you want to handle errors at a higher level)
 */
export function unwrap<T>(result: ApiResult<T>): T {
  if (isError(result)) {
    throw result.error;
  }
  return result.data;
}

/**
 * Unwrap result or return default value
 */
export function unwrapOr<T>(result: ApiResult<T>, defaultValue: T): T {
  if (isError(result)) {
    return defaultValue;
  }
  return result.data;
}

/**
 * Retry an async operation with exponential backoff
 */
export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  operationName: string,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<ApiResult<T>> {
  let lastError: unknown;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const result = await operation();
      return success(result);
    } catch (error) {
      lastError = error;

      if (attempt < maxRetries - 1) {
        const delay = initialDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  return handleApiError(operationName, lastError);
}